/** 계정 길드 — 소속·표식·기부·명예/금고 원장을 서버 확정값으로 표시한다. */
import { S, esc } from '../../core/state.js';
import { fold, loadingOr, muted, num, sec } from './common.js';
import { guildInteger, guildMoneyText } from '../guild-money.js';
import { GUILD_EFFECT_NAMES, guildEffectText, guildBuildingProgress } from '../guild-buildings.js';
import { guildShopPanel, guildShopStatus } from './guild-shop.js';
import { levelLabel } from '../../core/level-label.js';
import { nativeActionAllowed, townActionAllowed } from '../../core/net.js';
import { josa } from '../../world/josa.js';

const safeId = (v) => String(v || '').replace(/[^a-z0-9-]/gi, '');
const safeNativeId = (v) => String(v || '').replace(/[^a-z0-9:_-]/gi, '');
const ROLE = { master: '길드장', officer: '부길드장', member: '길드원' };

/**
 * LAUNCH-R1/R2 (DESIGN 14.302) — 네이티브(러스트) 필드에서 길드 쓰기는 두 갈래로 갈린다.
 * ① **회원 구성**(창설·가입 신청/취소·설정·역할·위임·추방·탈퇴 — `MEMBER_OFF`) 은
 * 내 지갑·인벤토리·좌표를 건드리지 않는 순수 D1 계정/길드 행이라, LAUNCH-R2 가 새
 * class-A 다리(`net.js` `FIELD_WRITE_ACTIONS` → `/api/field/write`)로 어디서나 연다.
 * ② **금고/상점/영지/초대-수락**(기부·건물 투자·영지 구매·이동·상점·아이템 이동·초대·
 * 수락/거절·가입 승인/거절 — `WRITE_OFF`) 은 몸(은화·인벤)을 직접 건드리거나 두 계정이
 * 동시에 얽히는 자리라 여전히 마을에서만 연다(설계는 남겼지만 구현은 아직이다 — 원장
 * `docs/UNIGE-2-LAUNCH-READINESS.md` §1 3단계 A/B/C 분류 참조).
 */
let WRITE_OFF = false;
let MEMBER_OFF = false;
/** 1.0 과 같게 마을에서만 되는 것(기부·길드 창고 이동·영지 이동) — 사냥터에선 끈다(사용자 결정 2026-09-27). */
let TOWN_ONLY_OFF = false;
const dis = (extra) => (WRITE_OFF || extra) ? 'disabled' : '';
const disM = (extra) => (MEMBER_OFF || extra) ? 'disabled' : '';
function nativeFieldNotice() {
  if (!WRITE_OFF && TOWN_ONLY_OFF) {
    return `<div class="guildwait" role="status">길드 기부·길드 창고 이동·영지 이동은 마을에서만 할 수 있다.
    <br><small>금고 자금 건물 투자·영지 구매·길드 상점·초대와 가입 처리는 사냥터에서도 된다.</small></div>`;
  }
  if (!WRITE_OFF) return '';
  return `<div class="guildwait" role="status">지금 있는 사냥터에서는 길드 금고·영지·상점·초대 응답을 바꿀 수 없다 — 조회와 회원 구성(창설·가입 신청·설정·역할·탈퇴)만 가능하다.
    <br><small>기부·투자·영지·상점·초대 수락/거절은 마을로 돌아가면 다시 열린다.</small></div>`;
}

function left(epoch) {
  const s = Math.max(0, Math.ceil(Number(epoch || 0) - Date.now() / 1000));
  if (!s) return '사용 가능';
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.ceil((s % 3600) / 60);
  return `${d ? d + '일 ' : ''}${h ? h + '시간 ' : ''}${m}분 뒤`;
}

function when(epoch) {
  const s = Math.max(0, Math.floor(Date.now() / 1000 - Number(epoch || 0)));
  if (s < 60) return '방금';
  if (s < 3600) return `${Math.floor(s / 60)}분 전`;
  if (s < 86400) return `${Math.floor(s / 3600)}시간 전`;
  return `${Math.floor(s / 86400)}일 전`;
}

function mark(g, title = true) {
  return `<span class="guildmark" style="--guild:${esc(g.color || '#d4a83c')}"`
    + `${title ? ` title="${esc(g.name || '')}"` : ''}>${esc(g.symbol || '◆')}</span>`;
}

function tabs() {
  const rows = [
    ['overview', '내 길드'], ['members', '함께하는 사람'], ['buildings', '영지·건설'], ['shop', '명예 상점'], ['ledger', '기록'],
  ];
  return `<nav class="guildtabs" aria-label="길드 메뉴">${rows.map(([id, name]) =>
    `<button class="${S.guildView === id ? 'on' : ''}" onclick="act.guildView('${id}')">${name}</button>`).join('')}</nav>`;
}

function optionPick(options, selected, action, kind) {
  return `<div class="guildoptions ${kind}">${(options || []).map((o) =>
    `<button class="${selected === o.id ? 'on' : ''}" title="${esc(o.name)}"`
      + ` style="${kind === 'colors' ? `--guild:${esc(o.hex)}` : ''}"`
      + ` onclick="${action}('${safeId(o.id)}')">${kind === 'colors' ? '<i></i>' : esc(o.symbol)}</button>`).join('')}</div>`;
}

/** 2026-10-25 — 재가입 대기는 떠난 그 길드에만 걸린다(서버 `rejoinOpenSql`). 다른 길드 신청·새 길드 창설은 막지 않는다 */
const rejoinBlocked = (g, guildId) => Number(g.cooldownUntil) > Date.now() / 1000 && !!guildId && guildId === g.cooldownGuildId;

function createBox(g) {
  const d = S.guildDraft;
  return sec('새 길드 만들기',
    `<label class="guildfield">길드명 <small>한글·영문·숫자 2~12자</small>
      <input id="guildnewname" maxlength="12" value="${esc(d.name)}" placeholder="길드 이름" oninput="act.guildName(this.value)"></label>
     <label class="guildfield">소개 <small id="guildintrocount">${[...d.intro].length}/80</small>
      <textarea maxlength="80" placeholder="어떤 길드인지 한 문장으로" oninput="act.guildIntro(this.value)">${esc(d.intro)}</textarea></label>
     <div class="guildlabel">표식 문양</div>${optionPick(g.options?.emblems, d.emblem, 'act.guildEmblem', 'emblems')}
     <div class="guildlabel">표식 색</div>${optionPick(g.options?.colors, d.color, 'act.guildColor', 'colors')}
     <div class="guildcreatepreview">${mark({ name: d.name || '새 길드', symbol: g.options?.emblems?.find((x) => x.id === d.emblem)?.symbol,
       color: g.options?.colors?.find((x) => x.id === d.color)?.hex })}<b id="guildnamepreview">${esc(d.name || '새 길드')}</b></div>
     <button id="guildcreate" class="gold wide" ${[...d.name.trim()].length < 2 ? 'disabled' : disM()} onclick="act.guildCreate()">길드 만들기</button>`
    + muted('같은 계정의 캐릭터가 길드를 함께 쓴다 · 길드장은 위임해야 탈퇴할 수 있다'));
}

function noGuild(g) {
  let h = '';
  if (!g.linked) {
    h += sec('계정 연결이 필요하다', muted('길드 가입에는 Google 또는 네이버 계정 연결이 필요하다. 같은 유니지 계정의 모든 캐릭터가 길드 소속을 공유한다.')
      + '<button class="gold wide" onclick="act.account()">계정 연결로 이동</button>');
    return h;
  }
  if (Number(g.cooldownUntil) > Date.now() / 1000) {
    h += `<div class="guildwait">${g.cooldownGuildName ? `「${esc(g.cooldownGuildName)}」 ` : '떠난 길드 '}재가입 대기 · <b>${left(g.cooldownUntil)}</b><br><small>다른 길드에는 지금 바로 가입·창설할 수 있다. 떠난 길드에만 72시간 뒤 다시 들어간다. 가입한 뒤 24시간이 지나면 버프와 명예 상점을 이용할 수 있다.</small></div>`;
  }
  if (g.invites?.length) h += sec('받은 초대', g.invites.map((x) => `
    <article class="guildrow"><div>${mark(x)}<b>${esc(x.name)}</b><small>${esc(x.intro || '소개 없음')} · ${left(x.expires_at)} 만료</small></div>
    <footer><button class="gold" ${dis()} onclick="act.guildAccept('${safeId(x.id)}')">가입</button>
    <button ${dis()} onclick="act.guildDecline('${safeId(x.id)}')">거절</button></footer></article>`).join(''));
  if (g.application) {
    h += sec('가입 신청', `<article class="guildrow"><div>${mark(g.application)}<b>${esc(g.application.name)}</b><small>${when(g.application.created_at)} 신청 · 답을 기다리는 중</small></div>
      <footer><button ${disM()} onclick="act.guildCancel()">신청 취소</button></footer></article>`);
  }
  h += sec('모집 중인 길드', (g.browse || []).length ? (g.browse || []).map((x) => `
    <article class="guildrow"><div>${mark(x)}<b>${esc(x.name)}</b><small>${num(x.members)}명 · ${esc(x.intro || '소개 없음')}</small></div>
    <footer>${x.recruiting
      ? (rejoinBlocked(g, x.id) ? `<button disabled>${left(g.cooldownUntil)} 재가입</button>`
        : `<button ${g.application ? 'disabled' : disM()} onclick="act.guildApply('${safeId(x.id)}')">가입 신청</button>`)
      : '<button disabled>모집 마감</button>'}</footer></article>`).join('') : muted('아직 만들어진 길드가 없다.'));
  h += '<details class="ux-details" id="guild-create"><summary>새 길드 만들기</summary>' + createBox(g) + '</details>';
  return h;
}

/**
 * 원주민 길드도 일반 길드와 같은 "하나뿐인 소속" 이다. 신청 버튼은 서버가 목록과 함께 준 내 소속(`S.nativeMembership`)이
 * 둘 다 비어 있을 때만 열린다 — 길드장이든 길드원이든 계정 길드가 있으면 닫히고, 내 원주민회 행에는 탈퇴 길이 붙는다.
 * 소속 응답이 아직 없으면(null) 버튼을 열지 않는다. 화면이 스스로 추론해 연 버튼이 실물 결함이었다(2026-09-07).
 */
function nativeDirectory(onlyMine = false) {
  const all = Array.isArray(S.nativeGuilds) ? S.nativeGuilds : [];
  const rows = onlyMine ? all.filter((x) => x.joined) : all;
  const mine = S.nativeMembership, human = mine?.human || null, native = mine?.native || null;
  if (!rows.length && !native) return '';
  const leaveButton = (id, name) => `<button class="danger" ${dis()} onclick="act.nativeGuildLeave('${safeNativeId(id)}','${esc(name)}')">탈퇴</button>`;
  const heldText = human ? `이미 길드 「${human.name}」에 속해 있다 — 길드는 하나만.`
    : native ? `이미 원주민회 「${native.name}」에 속해 있다 — 길드는 하나만.` : '';
  const list = rows.map((x) => {
    const hostile = x.humanStance === 'hostile';
    const action = x.joined ? leaveButton(x.id, x.name)
      : !mine ? '<button disabled>소속 확인 중</button>'
      : heldText ? '<button disabled>이미 길드 소속</button>'
      : hostile ? '<button disabled>사람을 받지 않음</button>'
      : x.application?.status === 'rejected' ? '<button disabled>신청 거절</button>'
      : `<button class="gold" ${dis()} onclick="act.nativeGuildApply('${safeNativeId(x.id)}')">가입 신청</button>`;
    const state = x.joined ? '내가 속한 원주민회다.'
      : !mine ? '소속을 확인하는 중이다.'
      : heldText ? heldText
      : hostile ? '이 길드는 이세계 전승자를 배척한다.'
      : x.application?.status === 'rejected' ? (x.application.reason === 'stranger' ? '아직 신뢰를 얻지 못했다.' : '길드가 신청을 거절했다.')
      : '가까워진 원주민이 있으면 받아줄 수 있다.';
    return `<article class="guildrow"><div><b>${esc(x.name)}</b><small>${num(x.members)}/${num(x.maxMembers)}명 · ${esc(state)}</small></div><footer>${action}</footer></article>`;
  });
  // 조직 운영이 꺼져 목록이 비어도 내 원주민회 행은 남긴다 — 탈퇴 길이 없으면 사람 길드 문까지 영영 닫힌다
  if (native && !rows.some((x) => x.joined)) {
    list.unshift(`<article class="guildrow"><div><b>${esc(native.name)}</b><small>내가 속한 원주민회다.</small></div><footer>${leaveButton(native.id, native.name)}</footer></article>`);
  }
  const dual = human && native
    ? `<div class="guildwait">길드 「${esc(human.name)}」${josa(String(human.name ?? ''), '와')} 원주민회 「${esc(native.name)}」 두 곳에 속해 있다.<br><small>길드 중복 가입은 불가능하다. 원주민회를 탈퇴해야 한다.</small></div>` : '';
  return sec(onlyMine ? '내 원주민 길드' : '원주민 길드 찾아보기', dual + list.join('')
    + muted('사람 길드·원주민회를 통틀어 한 곳만 · 옮기려면 먼저 탈퇴'));
}

function settings(g) {
  const me = g.me, guild = me.guild, lead = me.role === 'master', manage = lead || me.role === 'officer';
  if (!manage) return '';
  const emblems = g.options?.emblems || [], colors = g.options?.colors || [];
  return sec('길드 설정',
    `<label class="guildfield">소개 <small>${[...String(guild.intro || '')].length}/80</small>
      <textarea maxlength="80" oninput="act.guildLiveIntro(this.value)">${esc(guild.intro || '')}</textarea></label>
     <label class="guildcheck"><input type="checkbox" ${guild.recruiting ? 'checked' : ''} ${disM()} onchange="act.guildRecruiting(this.checked)"> 가입 신청 받기</label>`
    + (lead ? `<div class="guildlabel">표식 문양</div>${optionPick(emblems, guild.emblem, 'act.guildLiveEmblem', 'emblems')}
      <div class="guildlabel">표식 색</div>${optionPick(colors, guild.colorId, 'act.guildLiveColor', 'colors')}` : '')
    + `<button class="wide" ${disM()} onclick="act.guildSettings()">설정 저장</button>`);
}

function overview(g) {
  const me = g.me, guild = me.guild;
  return `<section class="ux-hero"><p class="ux-kicker">내 길드 · ${esc(me.roleName || ROLE[me.role])}</p><h3 class="ux-title">${mark(guild, false)} ${esc(guild.name)}</h3>
    <p class="ux-summary">${num(g.members?.length || 0)}명이 함께한다 · ${guild.status === 'active' ? '혜택 ' + left(me.benefitsAt) : '길드 동결 중'}</p></section>`
    + `<div class="ux-facts">
      <div class="ux-fact"><span>내 명예</span><b${g.economy ? ' class="ux-number"' : ''}>${g.economy ? guildMoneyText(g.economy.honor) : '확인 중'}</b></div>
      <div class="ux-fact"><span>길드 금고 · 은화</span><b${g.economy ? ' class="ux-number"' : ''}>${g.economy ? guildMoneyText(g.economy.treasury) : '확인 중'}</b></div>
    </div>`
    + muted('버프·명예 상점은 가입 24시간 뒤부터')
    + activeBenefits(g)
    + `<div class="ux-actions"><button onclick="act.guildView('members')">길드원 만나보기</button><button onclick="act.guildView('buildings')">영지·건설 살펴보기</button><button onclick="act.guildView('shop')">내 명예 쓰기</button></div>`
    + (g.estate?.travel ? `<button class="ux-primary" ${dis(TOWN_ONLY_OFF)} onclick="act.guildEstateTravel()">우리 영지로 이동 · 무료</button>` : '')
    + (g.applications?.length && ['master', 'officer'].includes(me.role) ? `<p class="ux-note">가입을 기다리는 사람이 ${num(g.applications.length)}명 있다. <button onclick="act.guildView('members')">가입 신청 살펴보기</button></p>` : '')
    + (guild.intro ? '<details class="ux-details" id="guild-intro"><summary>길드 소개 읽기</summary><p>' + esc(guild.intro) + '</p></details>' : '')
    + '<details class="ux-details" id="guild-funding"' + (S.guildMoneyConfirmation || S.guildMoneyPending || S.guildMoneyDraft?.mode === 'allocate' ? ' open data-force-open="true"' : '') + '><summary>공동 금고에 보태기 · 잔액 살펴보기</summary>' + muted('기부는 공동 금고에 모인다. 건물에 쓸 자금은 길드장이 따로 배정한다.') + economySummary(g) + moneyForm(g) + '</details>'
    + (['master', 'officer'].includes(me.role) ? '<details class="ux-details" id="guild-settings"><summary>소개·모집·길드 표식 바꾸기</summary>' + settings(g) + '</details>' : '');
}

function economySummary(g) {
  const e = g.economy;
  if (!e) return muted('기부 기록을 불러오지 못했다. 새로고침해 다시 확인해 달라.');
  return `<div class="guildsummary guildmoney-summary">
    <div><span>내 명예</span><b class="ux-number">${guildMoneyText(e.honor)}</b></div>
    <div><span>내 누적 기부 · 은화</span><b class="ux-number">${guildMoneyText(e.myContribution)}</b></div>
    <div><span>길드 금고 · 은화</span><b class="ux-number">${guildMoneyText(e.treasury)}</b></div>
  </div><button class="wide" onclick="act.guildRefresh()">잔액·기부 상태 새로고침</button>`;
}

function moneyStatus() {
  let h = S.guildMoneyNotice ? `<p class="guildmoney-notice" role="status">${esc(S.guildMoneyNotice)}</p>` : '';
  const p = S.guildMoneyPending;
  if (!p || p.charId !== S.charId) return h;
  const action = p.request.t === 'guildEstateBuy' ? '영지 구매' : p.request.t === 'guildAllocate' ? '건물 투자' : '금고 기부';
  h += `<section class="guildwait" aria-live="polite"><b>${guildMoneyText(p.request.silver)} 은화 · ${p.status === 'waiting' ? action + ' 결과 확인 중…' : '결과 확인이 필요하다'}</b>
    <p>처리 중인 ${action} 결과를 먼저 확인해 달라. 다시 확인해도 은화는 중복 차감되지 않는다.</p>
    <button class="wide" ${p.status === 'waiting' ? 'disabled' : ''} onclick="act.guildMoneyRetry()">${p.status === 'waiting' ? '응답 대기 중' : action + ' 결과 확인'}</button></section>`;
  return h;
}

function moneyForm(g) {
  const e = g.economy;
  if (!e) return '';
  const confirmation = S.guildMoneyConfirmation;
  if (confirmation?.charId === S.charId && confirmation.request.guildId === g.me.guild.id) {
    return sec(confirmation.request.t === 'guildEstateBuy' ? '영지 구매 최종 확인' : confirmation.request.t === 'guildAllocate' ? '건물 투자 최종 확인' : '금고 기부 최종 확인', `<div class="guildwait" role="status">${esc(confirmation.question)}</div>`
      + `<div class="guildmoney-modes"><button ${dis()} onclick="act.guildMoneyCancel()">취소</button>`
      + `<button class="gold" ${dis()} onclick="act.guildMoneyConfirm()">확인 · 실행</button></div>`);
  }
  const d = S.guildMoneyDraft, lead = g.me.role === 'master', allocating = lead && d.mode === 'allocate';
  const amount = guildInteger(d.amount), valid = amount > 0n && amount % 100n === 0n;
  const busy = !!(S.guildMoneyPending || S.guildShopPending);
  const allowed = allocating ? lead && e.canAllocate : e.canDonate;
  const target = d.building;
  return sec(allocating ? '금고 자금으로 건물 투자' : '길드 금고에 기부',
    (lead ? `<div class="guildmoney-modes">
      <button class="${allocating ? '' : 'on'}" ${busy ? 'disabled' : ''} onclick="act.guildMoneyMode('donate')">금고에 기부</button>
      <button class="${allocating ? 'on' : ''}" ${busy ? 'disabled' : ''} onclick="act.guildMoneyMode('allocate')">건물에 투자</button></div>` : '')
    + (allocating ? `<label class="guildfield">투자할 건물<select ${busy ? 'disabled' : ''} onchange="act.guildMoneyTarget(this.value)">
      ${(e.buildings || []).map((b) => `<option value="${safeId(b.id)}" ${target === b.id ? 'selected' : ''}>${esc(b.name)}</option>`).join('')}</select></label>` : '')
    + `<label class="guildfield">은화 <small>${allocating ? '길드 금고 잔액' : '내 지갑 + 창고'} ${guildMoneyText(allocating ? e.treasury : e.usableSilver)}</small>
        <input id="guild-money-amount" type="text" inputmode="numeric" autocomplete="off" maxlength="100"
          value="${esc(d.amount)}" ${busy ? 'disabled' : ''} oninput="act.guildMoneyAmount(this.value)" aria-describedby="guild-money-validation"></label>
      <div class="guildmoney-quick">${[['10000','+10,000'],['100000','+100,000'],['1000000','+1,000,000']].map(([n,label]) =>
        `<button ${busy ? 'disabled' : ''} onclick="act.guildMoneyAdd('${n}')">${label}</button>`).join('')}
        <button ${busy ? 'disabled' : ''} onclick="act.guildMoneyMax()">모두</button></div>
      <p id="guild-money-validation" class="muted">${valid ? (allocating ? '길드 금고에서 선택한 건물에 투자 · 추가 명예 없음' : '내 지갑에서 먼저 내고 부족분은 창고에서 사용 · 전액 길드 금고 입금') : '100 은화 단위로 입력해 달라.'}</p>
      <div class="guildmoney-preview"><span>받을 명예</span><b id="guild-money-honor">${valid ? guildMoneyText(allocating ? 0n : amount / 100n) : '—'}</b></div>
      ${allowed ? '' : muted(allocating ? '지금은 건물에 투자할 수 없다.' : e.donateUnavailableReason || '지금은 기부할 수 없다.')}
      <button id="guild-money-submit" class="gold wide" ${busy || !allowed || !valid ? 'disabled' : dis()} onclick="act.guildMoneySubmit()">${allocating ? '금고 자금 투자' : '길드 금고에 기부'}</button>`
    // 2026-10-21 — 되돌릴 수 없는 것(환불 불가)은 버튼 바로 밑 한 줄로 남긴다
    + muted(allocating ? '길드장만 투자한다 · 명예는 더 주지 않는다'
      : '100 은화당 명예 1 · <b>환불 불가</b> · 탈퇴해도 명예는 남는다'));
}

function estates(g) {
  const e=g.estate;if(!e)return muted('영지 계약 정보를 불러오지 못했다.');
  return sec('길드 영지 부지',fold('길드장이 금고로 한 곳을 산다 · 세계가 바뀌어도 남는다','모든 회원의 기부는 길드 금고에 모이며, 길드장은 같은 금고 자금으로 길드당 한 곳을 구매한다. 소유권과 기존 길드 건물 투자는 세계 초기화 후에도 남는다. 성문은 소유 길드원에게만 열리며 상인과 기존 길드 서비스가 연결된다. 공개 마을에서 무료 전용 이동을 이용할 수 있다.')
    +(e.travel?`<button ${dis(TOWN_ONLY_OFF)} onclick="act.guildEstateTravel()">우리 영지로 이동 · 무료</button>`:'')
    +(S.you?.guildEstateId?`<button ${dis(TOWN_ONLY_OFF)} onclick="act.guildEstateTravel(true)">공개 마을로 돌아가기</button>`:'')
    +(e.plots||[]).map(p=>'<article class="guildrow"><div><b>'+esc(p.name||p.id)+'</b><small>좌표 '+p.x+', '+p.y+' · '+(p.mine?'우리 길드 소유':p.ownerName?esc(p.ownerName)+' 소유':'미계약')+'</small></div><footer>'
      +(p.ownerName?'':'<button '+(!e.canPurchase||S.guildMoneyPending||S.guildShopPending?'disabled':dis())+' onclick="act.guildEstateBuy(\''+safeId(p.id)+'\')">'+guildMoneyText(e.purchaseSilver)+' 은화로 구매</button>')+'</footer></article>').join(''));
}
function buildings(g) {
  const e = g.economy;
  if (!e) return muted('건물 투자 기록을 불러오지 못했다. 새로고침 뒤 확인해 달라.');
  const lead = g.me.role === 'master';
  return estates(g) + economySummary(g) + activeBenefits(g) + (e.buildings || []).map((b) => {
    const progress = guildBuildingProgress(b), f = b.effect;
    return `<section class="guildproject"><header><h3>${esc(b.name)}</h3><strong>Lv.${levelLabel(b.level)}</strong></header>`
      + (f ? `<div class="guildproject-effect"><span>${esc(GUILD_EFFECT_NAMES[f.stat] || '길드 효과')}</span>
        <b>+${esc(guildEffectText(f.current))}${f.unit === '%' ? '%' : ''}</b></div>
        <p class="guildproject-next">다음 +${esc(guildEffectText(f.next))}${f.unit === '%' ? '%' : ''}
        <small>증가 +${esc(guildEffectText(f.delta))}${f.unit === '%' ? '%p' : ''}</small></p>` : '')
      + `<progress max="10000" value="${progress.value}" aria-label="${esc(b.name)} 건설 진행률"></progress>
        <div class="guildproject-funds"><span>모인 은화 <b>${guildMoneyText(b.funded)}</b></span>
        <span>필요 은화 <b>${guildMoneyText(b.nextCost)}</b></span></div>
        <p class="guildproject-next">${b.pending ? '자금 충족 · 이어서 건설 중' : `완성까지 ${guildMoneyText(progress.needed)} 은화`}</p>
        ${lead ? `<button class="wide" ${S.guildMoneyPending ? 'disabled' : dis()} onclick="act.guildMoneyBuilding('${safeId(b.id)}')">금고 자금을 이 건물에 투자</button>` : ''}</section>`;
  }).join('') + muted(lead
    ? '길드장만 금고 자금을 건물에 투자한다 · 다 모이면 자동 건설 · 남은 은화는 다음 레벨로'
    : '회원은 개요에서 금고에 기부한다 · 건물 투자는 길드장 몫');
}

function activeBenefits(g) {
  const rows = (g.economy?.buildings || []).filter((b) => b.effect);
  if (!rows.length) return '';
  const now = Date.now() / 1000, waiting = Number(g.me.benefitsAt) > now;
  // 관리자 캐릭터 제외는 2026-09-09 폐기됐다(DESIGN 14.153.3) — 남은 이유는 동결과 대기뿐이다.
  const reason = g.me.guild.status !== 'active' ? '길드가 동결되어 버프가 멈춰 있다.'
    : waiting ? `길드 버프는 ${left(g.me.benefitsAt)} 적용된다.` : '가입 혜택 적용 중 · 건물 레벨에 따라 버프 효과 증가';
  return `<section class="guildbenefits"><h3>길드 버프</h3><p>${esc(reason)}</p><div>${rows.map((b) =>
    `<span>${esc(GUILD_EFFECT_NAMES[b.effect.stat] || b.name)} <b>+${esc(guildEffectText(b.effect.current))}${b.effect.unit === '%' ? '%' : ''}</b></span>`).join('')}</div></section>`;
}

function members(g) {
  const me = g.me, power = me.role === 'master' ? 3 : me.role === 'officer' ? 2 : 1;
  let h = '';
  if (power >= 2) h += sec('회원 초대', `<div class="row"><input maxlength="12" placeholder="정확한 캐릭터 이름" value="${esc(S.guildDraft.invite)}" oninput="act.guildInviteName(this.value)">
    <button ${S.guildDraft.invite.trim() ? dis() : 'disabled'} onclick="act.guildInvite()">초대</button></div>`
    + muted('Google·네이버를 연결한 계정만 · 72시간 유효'));
  if (power >= 2 && g.applications?.length) h += sec('가입 신청', g.applications.map((a) => `
    <article class="guildrow"><div><b>${esc(a.character_name)}</b><small>${when(a.created_at)} 신청</small></div>
    <footer><button class="gold" ${dis()} onclick="act.guildApprove('${safeId(a.id)}')">승인</button>
    <button ${dis()} onclick="act.guildReject('${safeId(a.id)}')">거절</button></footer></article>`).join(''));
  h += sec(`회원 ${num(g.members?.length || 0)}명`, (g.members || []).map((x) => {
    const mine = x.id === me.id, canRole = me.role === 'master' && !mine && x.role !== 'master';
    const canKick = !mine && x.role !== 'master' && (me.role === 'master' || (me.role === 'officer' && x.role === 'member'));
    return `<article class="guildrow"><div><b>${esc(x.display_name)} ${mine ? '<em>나</em>' : ''}</b>
      <small>${esc(x.roleName || ROLE[x.role])} · 마지막 접속 ${when(x.last_seen)}</small></div><footer>`
      + (canRole ? `<button ${disM()} onclick="act.guildRole('${safeId(x.id)}','${x.role === 'officer' ? 'member' : 'officer'}')">${x.role === 'officer' ? '길드원으로' : '부길드장으로'}</button>
        <button ${disM()} onclick="act.guildTransfer('${safeId(x.id)}','${esc(x.display_name)}')">길드장 위임</button>` : '')
      + (canKick ? `<button class="danger" ${disM()} onclick="act.guildKick('${safeId(x.id)}','${esc(x.display_name)}')">추방</button>` : '')
      + '</footer></article>';
  }).join(''));
  if (me.role !== 'master') h += `<button class="danger wide" ${disM()} onclick="act.guildLeave()">길드 탈퇴</button>`;
  else h += muted('길드장은 위임한 뒤 탈퇴할 수 있다');
  return h;
}

function ledger(g) {
  const KIND = { create: '길드 창설', join: '가입', leave: '탈퇴', kick: '추방', promote: '승급', demote: '역할 변경', transfer: '길드장 위임' };
  const kinds = { treasury: '길드 금고 기부', building: '옛 건물 직접 후원', allocation: '금고 자금 건물 투자' };
  const money = (g.economy?.ledger || []);
  const h = sec('기부·투자 기록', money.length ? money.map((x) => `<article class="guildmoney-ledger">
    <header><b>${esc(x.name)}</b><span>${when(x.at)}</span></header>
    <p>${esc(kinds[x.kind] || '원장')} ${x.building ? `· ${esc(g.economy.buildings?.find((b) => b.id === x.building)?.name || x.building)}` : ''}</p>
    <div><strong>${guildMoneyText(x.silver)} 은화</strong><span>명예 +${guildMoneyText(x.honor)}</span></div>
    <small>세계 ${guildMoneyText(x.worldN)} · 영수증 ${esc(x.id)}</small></article>`).join('') : muted('아직 기부·투자 기록이 없다.'));
  return h + sec('길드원 활동 기록', (g.events || []).length ? g.events.map((x) => `
    <div class="guildevent"><span>${when(x.at)}</span><b>${esc(x.character_name)}</b><em>${esc(KIND[x.kind] || x.kind)}</em>
    ${x.role_before || x.role_after ? `<small>${esc(ROLE[x.role_before] || x.role_before || '없음')} → ${esc(ROLE[x.role_after] || x.role_after || '없음')}</small>` : ''}</div>`).join('')
    : muted('아직 길드원 활동 기록이 없다.'));
}

export function render() {
  const g = S.guild;
  if (!g) return loadingOr('guildGet', '길드 기록을 불러오는 중…');
  if (!g.feature) return muted('길드 기능은 운영자 시험 뒤에 열린다.');
  if (g.error) return `<div class="boarderror">${esc(g.error)}<button onclick="act.guildRefresh()">새로고침</button></div>`;
  // 대표 액션을 기부로 잡으면 사냥터에서 이미 되는 class-A(14.314·14.317)까지 전부 꺼졌다.
  WRITE_OFF = !nativeActionAllowed('guildInvite');
  // DESIGN 14.599 6단계 — 원천 소켓이거나 게임 서버의 마을 구역이면 기부·귀속 장비 이동·영지 이동이 된다(사냥터 소켓만 막는다)
  TOWN_ONLY_OFF = !townActionAllowed('guildDonate');
  MEMBER_OFF = !nativeActionAllowed('guildCreate');
  let h = g.canary ? '<div class="guildcanary">운영자 시험 중 · 일반 사용자에게는 아직 보이지 않는다</div>' : '';
  h += moneyStatus() + guildShopStatus();
  // Q276 — 공성전(원주민회 회원의 참전 영역 포함)은 [행사] › 공성전 한 곳으로 옮겼다(전에는 이 창의 탭)
  if (!g.me) {
    if (S.nativeMembership?.native) return h + nativeDirectory(true) + nativeFieldNotice()
      + '<details class="ux-details" id="guild-native-directory"><summary>다른 원주민 길드 살펴보기</summary>' + nativeDirectory() + '</details>';
    return h + '<section class="ux-hero"><p class="ux-kicker">함께 살아갈 사람들</p><h3 class="ux-title">길드를 찾아보자</h3><p class="ux-summary">소개를 읽고 함께할 길드에 신청하거나, 받은 초대로 합류할 수 있다.</p></section>'
      + nativeFieldNotice() + noGuild(g) + '<details class="ux-details" id="guild-native-directory"><summary>원주민 길드도 살펴보기</summary>' + nativeDirectory() + '</details>';
  }
  h += tabs() + nativeFieldNotice();
  if (S.guildView === 'overview') h += overview(g);
  else if (S.guildView === 'members') h += members(g);
  else if (S.guildView === 'ledger') h += ledger(g);
  else if (S.guildView === 'buildings') h += buildings(g);
  else h += guildShopPanel(g);
  return h + (S.nativeMembership?.native ? nativeDirectory(true) : '')
    + '<details class="ux-details" id="guild-native-directory"><summary>원주민 길드 찾아보기</summary>' + nativeDirectory() + '</details>';
}
